import { format } from 'date-fns';
import {
  DocumentEvidenceLink,
  DocumentEvidenceStatus
} from '../types/Documentation.types';
import { formatDateOnly } from './Date.util';

// Reading the API's document -> obligation linkage (EAA-1938).
//
// Evidence is append-only, so replacing a bias audit report leaves both files in the Documents
// tab. The API says which one the obligation is actually using; this module is the one place that
// decides what to call it and how to word the explanation, so the badge and the tooltip cannot
// drift apart (same reason `evidenceUploadLabel` and the date-rule messages are centralized).

// Strongest first. The API already orders links this way; re-deriving it here keeps the rule
// visible on the client and means a payload that arrives unordered still renders sensibly.
const STATUS_RANK: Record<DocumentEvidenceStatus, number> = {
  CURRENT: 0,
  LATEST: 1,
  SUPERSEDED: 2
};

export const primaryEvidenceLink = (
  links?: DocumentEvidenceLink[] | null
): DocumentEvidenceLink | undefined => {
  if (!links?.length) return undefined;
  return links.reduce((best, link) =>
    STATUS_RANK[link.status] < STATUS_RANK[best.status] ? link : best
  );
};

// An instant, with the time kept: two replacements on the same day must stay distinguishable.
// Same shape as the DATE CREATED column, which delegates here so the row and its tooltip cannot
// disagree about how a date looks.
export const formatInstant = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : format(parsed, 'dd MMM yyyy, HH:mm');
};

// " Audit dated 12 Mar 2026." — omitted entirely when the pack never collected a production date,
// so a candidate notice is not captioned with an audit date it never had.
const auditClause = (link: DocumentEvidenceLink): string => {
  const dated = link.attestedAt ? formatDateOnly(link.attestedAt) : '';
  return dated ? ` Audit dated ${dated}.` : '';
};

const sentence = (link: DocumentEvidenceLink): string => {
  const obligation = `"${link.obligationTitle}"`;

  if (link.status === 'SUPERSEDED') {
    // Never mentions today's status or expiry: a replaced document is not responsible for either,
    // and the API sends null for both on this link precisely so that cannot be got wrong.
    const replaced = formatInstant(link.supersededAt);
    return replaced
      ? `Superseded on ${replaced}. Was evidence for ${obligation}.${auditClause(link)}`
      : `Superseded. Was evidence for ${obligation}.${auditClause(link)}`;
  }

  if (link.status === 'LATEST') {
    // The obligation was reopened: evidence rows survive but `expiresAt` is cleared, so the honest
    // answer to "which document is this based on" is none.
    return (
      `Most recent submission for ${obligation}, ${formatInstant(link.submittedAt)}. ` +
      'The obligation was reopened, so no document currently sets its status or expiry.'
    );
  }

  const expiry = link.obligationExpiresAt
    ? formatDateOnly(link.obligationExpiresAt)
    : '';
  if (expiry && link.obligationStatus === 'EXPIRED') {
    return `Current evidence for ${obligation}.${auditClause(link)} The obligation expired on ${expiry}.`;
  }
  if (expiry) {
    return `Current evidence for ${obligation}.${auditClause(link)} Sets the obligation's expiry: ${expiry}.`;
  }
  return `Current evidence for ${obligation}.${auditClause(link)} Submitted ${formatInstant(link.submittedAt)}.`;
};

// One line per obligation, strongest first. A document can be evidence for more than one — the
// server only checks that it belongs to the same AI system — and the pill can only show one word,
// so the rest goes here.
export const evidenceLinkTooltip = (
  links?: DocumentEvidenceLink[] | null
): string => {
  if (!links?.length) return '';
  return [...links]
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
    .map(sentence)
    .join('\n');
};
